import React, { useState, useEffect } from "react";
import Icon from "../AppIcon";
import { useAuth } from "../../contexts/AuthContext";
import { notifications as notificationsAPI } from "../../lib/database";

const NotificationPanel = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
    }
  }, [user?.id]);

  // Refresh notifications when panel is opened
  useEffect(() => {
    if (isOpen && user?.id) {
      loadNotifications();
    }
  }, [isOpen, user?.id]);

  const loadNotifications = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const [notificationsData, unreadCountData] = await Promise.all([
        notificationsAPI.getByUserId(user.id, { limit: 50 }),
        notificationsAPI.getUnreadCount(user.id),
      ]);

      setNotifications(notificationsData || []);
      setUnreadCount(unreadCountData || 0);
    } catch (error) {
      console.error("Error loading notifications:", error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await notificationsAPI.markAsRead(notifId);
      setNotifications((prev) =>
        prev?.map((n) => (n?.id === notifId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;

    try {
      await notificationsAPI.markAllAsRead(user.id);
      setNotifications((prev) => prev?.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const clearNotification = async (notifId) => {
    try {
      await notificationsAPI.delete(notifId);
      const notif = notifications?.find((n) => n?.id === notifId);
      setNotifications((prev) => prev?.filter((n) => n?.id !== notifId));
      if (notif && !notif?.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "assignment":
        return "UserPlus";
      case "stage_update":
        return "TrendingUp";
      case "status_change":
        return "AlertCircle";
      case "work_completed":
        return "CheckCircle2";
      default:
        return "Bell";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600";
      case "medium":
        return "text-yellow-600";
      case "low":
        return "text-blue-600";
      default:
        return "text-gray-600";
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    return `${diffDays}d ago`;
  };

  return (
    <div className="notification-panel-container">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="notification-bell-button"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[1050]"
            onClick={() => setIsOpen(false)}
          />
          <div className="notification-dropdown">
            <div className="notification-header">
              <div className="flex items-center gap-2">
                <Icon name="Bell" size={18} />
                <h3 className="notification-title">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="notification-count-badge">
                    {unreadCount}
                  </span>
                )}
              </div>
              {notifications?.length > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="notification-mark-all-btn"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="notification-list">
              {isLoading ? (
                <div className="notification-empty">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500 mt-2">
                    Loading notifications...
                  </p>
                </div>
              ) : notifications?.length === 0 ? (
                <div className="notification-empty">
                  <Icon name="BellOff" size={40} className="text-gray-300" />
                  <p className="text-sm text-gray-500 mt-2">No notifications</p>
                </div>
              ) : (
                notifications?.map((notif) => (
                  <div
                    key={notif?.id}
                    className={`notification-item ${
                      !notif?.read ? "unread" : ""
                    }`}
                    onClick={() => !notif?.read && markAsRead(notif?.id)}
                  >
                    <div className="notification-item-icon">
                      <Icon
                        name={getNotificationIcon(notif?.type)}
                        size={18}
                        className={getPriorityColor(notif?.priority)}
                      />
                    </div>
                    <div className="notification-item-content">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="notification-item-title">
                          {notif?.title}
                        </h4>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            clearNotification(notif?.id);
                          }}
                          className="notification-close-btn"
                          aria-label="Clear notification"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                      <p className="notification-item-message">
                        {notif?.message}
                      </p>
                      <span className="notification-item-time">
                        {formatTimestamp(notif?.created_at)}
                      </span>
                    </div>
                    {!notif?.read && (
                      <div className="notification-unread-dot" />
                    )}
                  </div>
                ))
              )}
            </div>

            {notifications?.length > 0 && (
              <div className="notification-footer">
                <button className="notification-view-all-btn">
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationPanel;
